import {
	compact,
	debounce,
	every,
	filter,
	find,
	flatten,
	flatMapDeep,
	forEach,
	groupBy,
	map,
	minBy,
	orderBy,
	reduce,
	size,
	some,
	startsWith,
	uniq
} from "lodash";
import "./manage-report.less";
import template from "./manage-report.html";
import {
	FORM_REPORT_PREVIOUS,
	FORM_REPORT_NEXT,
	BASE_REPORT,
	EDIT_REPORT,
	NEW_REPORT
} from "routes.js";
import {
	EVENT_REPORT_LOCKED,
	EVENT_FILTER_CURRENT_OPTIONS,
	EVENT_PAGE_LOADED,
	EVENT_REPORT_STATUS_CHANGE,
	EVENT_REVERT_VALUES,
	EVENT_FILTER_PRIMARYDATE,
	EVENT_PROCESS_UI_RULES,
	EVENT_PROCESS_FILTER_RULES,
	EVENT_PROCESS_RULES_SETTLED,
	EVENT_PROCESS_VALIDATIONS,
	EVENT_SET_VALUE,
	EVENT_SET_DEFAULT_VALUE,
	EVENT_CLEAR_VALUE,
	EVENT_VALUE_CHANGED,
	EVENT_HOTKEY_LOCK,
	EVENT_TIMEZONE_SET,
	EVENT_REPORT_SAVED,
	EVENT_ENABLE_SUCCESSOR_LOCK,
	EVENT_DISABLE_SUCCESSOR_LOCK,
	EVENT_HANDLE_SUCCESSOR_LOCK,
	REPORT_SCOPES,
	FEATURE_TYPES,
	FIELD_TEMPLATES,
	PIVOT_TYPES,
	FORM_TIMEZONE_TYPES,
	TIME_ZONES,
	READ_ONLY_MODES,
	EVENT_REPORT_UNLOCKED,
	EVENT_DETAIL_DIRTY,
	EVENT_DETAIL_TOGGLE,
	EVENT_DETAILS_SUPPRESSION_TOGGLED,
	EVENT_SET_FORM_PRISTINE,
	REPORT_STATES,
	IS_NOT_AVAILABLE_VALUE,
	EVENT_TRIGGER_FILTER_RULES
} from "constants.js";
import {
	CONDITION_EVALUATIONS,
	ENGINE_EVALUATIONS,
	RULE_OPERATORS,
	TRIGGER_TYPES,
	UI_OPERATOR_TYPES,
	TRIGGER_OPERATOR_CODES
} from "screens/rule-provider/constants.js";
import moment from "moment";
import hotkeyConfigs from "../../services/hotkeyConfig";
import { hasHadState } from "../../utils";
import {
	confirmUnsavedChangesWillBeLost,
	confirmCompletionOnLeave,
	confirmWithdrawReport,
	confirmDirtySubmission,
	confirmRuleWarningsOnReport,
	notifyTimezoneDiscrepancy,
	notifyUserTimezoneError,
	notifyFormTimezoneError,
	notifyMultipleEntityTimezoneError,
	notifyNoEntityTimezoneError,
	showForceRefresh
} from "./prompts";
import { UNIQUE_TIMEZONE_ERRORS } from "../../services/entity-manager";
import { globs } from "../../services/globs";
import { REPORT_SCOPE_MODES } from "../report-scope/report-scope.js";
import { snapshotUsageUpdates } from "../../utils/dataUsage";
import multiSectionWarning from "./multi-section-warning/multi-section-warning";

import { v4 as uuid } from "uuid";

const headerValuesKey = "header";
const detailValuesKey = "details";

export function controller(
	$state,
	$uibModal,
	$logger,
	$scope,
	$transitions,
	$timeout,
	appState,
	api,
	detailHelper,
	loadingService,
	toastr,
	hotkeys,
	featureHelper,
	filterService,
	entityManager
) {
	const ctrl = this;

	ctrl.detailValuesKey = detailValuesKey;
	ctrl.errors = [];
	ctrl.reportScopes = REPORT_SCOPES;
	ctrl.permissions = appState.permissions.Reporting;
	ctrl._unsubscribe = () => {};
	ctrl.deregisterTransitionHook = () => {};
	ctrl.isProcessingRules = false;
	ctrl.isFormDirty = appState.forms.isDirty;

	ctrl.scope = $scope;
	ctrl.$onInit = onInit;
	ctrl.$onDestroy = onDestroy;
	ctrl.handleDelete = handleDelete;
	ctrl.handleWithdraw = handleWithdraw;
	ctrl.handleRestore = handleRestore;
	ctrl.handleDetailSave = debounce(handleDetailSave, 500, { leading: true });
	ctrl.handleDetailSelected = handleDetailSelected;
	ctrl.saveReport = debounce(handleSaveReport, 500, { leading: true });
	ctrl.handleSubmit = debounce(handleSubmit, 500, { leading: true });
	ctrl.setCallbacks = setCallbacks;
	ctrl.processRules = processRules;
	ctrl.runDetailsRules = runDetailsRules;
	ctrl.nextReport = debounce(nextReport, 500, { leading: true });
	ctrl.createReport = debounce(createReport, 500, { leading: true });
	ctrl.prevReport = debounce(prevReport, 500, { leading: true });
	ctrl.editDetail = editDetail;
	ctrl.triggerFilters = triggerFilters;
	ctrl.getAllValues = getAllValues;
	ctrl.isMinimalMode = isMinimalMode;
	ctrl.canDisplayCompleteActions = canDisplayCompleteActions;
	ctrl.validationScopes = [];
	ctrl.suppressDetails = false;
	ctrl.showAddDetail = true;
	ctrl.fakeDetailValidation = {};
	ctrl.detailModalIsOpen = false;

	ctrl.canCopyToNew = globs.reporting.types("POSITIVE").states("DELETED").copy;

	const submitEventBuilder = $logger.buildReportEvent("submit");
	const unsavedChangesEvent = $logger.buildReportEvent("browserNavigateAway")("requested");
	const reportSaveEventBuilder = $logger.buildReportEvent("save");

	ctrl.alertUserIfDirty = e => {
		// https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
		if(ctrl.isReportDirty) {
			// Cancel the event
			e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
			// Chrome requires returnValue to be set
			e.returnValue = "";

			$logger.event(unsavedChangesEvent, { report: ctrl.reportMetadata });
		}
	};

	ctrl.NEW_REPORT = NEW_REPORT;
	ctrl.loaded = false;

	function onInit() {
		ctrl.eventBus = ctrl.reportScope.events;

		ctrl.dirtyDetailTracking = {};
		ctrl.isReportDirty = false;
		ctrl.dirtyHeaderFields = {};

		ctrl._unsubscribe = ctrl.ruleProvider.subscribe(() => {
			const { errors } = ctrl.ruleProvider;
			ctrl.errors = errors;
		});

		const asyncInit = async() => {
			const form = ctrl.reportScope.form || {};

			initLocks(form.Fields);

			ctrl.form = {
				...form,
				reportFields: form.Fields.filter(x => !x.IsDetail && !x.IsNegative),
				detailFields: form.Fields.filter(x => x.IsDetail && !x.IsNegative),
				detailSection: form.Sections?.find(section => section.IsMulti),
				staticDetailFields: form.Fields.filter(x => x.IsDetail && !x.Pivot),
				pivotedDetailFields: form.Fields.filter(x => x.IsDetail && !!x.Pivot)
			};

			setReportMetadata();

			if(ctrl.reportScope.isIsolated && !ctrl.reportMetadata.isNew) {
				$state.current.title = `Edit ${ctrl.form.nomenclature.PositiveReport} #${ctrl.reportMetadata.report.Number}`;
			}

			ctrl.footerNomenclature = ctrl.form.nomenclature.ReportFooter;

			ctrl.validationService = ctrl.reportScope.validationService;

			ctrl.validationService.init(ctrl.form.Rules, ctrl.form.Fields);

			setFieldOptions(ctrl.form.Fields);
			setupTimezoneOverride(ctrl.form.Fields);

			if(!ctrl.isMinimalMode()) {
				setHotKeys();
			}

			// note: rules should be initialized before features so that features run after ux rules
			initializeRules();
			initializeFeatures(ctrl.form);

			registerEvents();

			angular.element(document).ready(function() {
				ctrl.loaded = true;
				ctrl.eventBus.trigger(EVENT_PAGE_LOADED);
			});

			if(!ctrl.isMinimalMode()) {
				window.vesl = window.vesl ?? {};
				window.vesl.getReportPayload = () => $logger.debug("current report payload", getAllValues(true));
				window.vesl.logDirtyChanges = false;
			}
		};

		asyncInit();
	}

	function isMinimalMode() {
		return ctrl.reportScope.mode === REPORT_SCOPE_MODES.minimal;
	}

	function shouldLogDirtyChanges() {
		return !ctrl.isMinimalMode() && window.vesl?.logDirtyChanges === true;
	}

	function setReportMetadata(newReport) {
		newReport = newReport || ctrl.report;
		ctrl.reportScope.handleReportUpdated(newReport);

		const reverseSortedHistory = (newReport?.History ?? [])
			.sort((a, b) => moment(a.Occurred).isBefore(b.Occurred) ? 1 : -1);

		ctrl.reportMetadata = {
			initial: JSON.stringify(newReport),
			report: {
				...newReport
			},
			validations: [],
			hasErrors: false,
			hasWarnings: false,
			isNew: !newReport || newReport.Id === undefined,
			reverseSortedHistory
		};

		ctrl.currentHistory = ctrl.reportMetadata.reverseSortedHistory?.[0];

		ctrl.reportRules = !!ctrl.reportMetadata.report.Id ? [...(ctrl.reportMetadata.report.Rules ?? [])] : [];
		ctrl.reportValues = groupReportValues();
		ctrl.primaryEntityName = setPrimaryEntityName();
	}

	function setFormUpdates(update, attemptedValues, initialValues) {
		const processedValues = update.IsNegative === true || update.IsDeleted === true ? [] : (update.Fields ?? attemptedValues);

		ctrl.reportMetadata.report = update;

		ctrl.form = appState.setRecentlyUsed(
			ctrl.form,
			processedValues,
			initialValues,
			update.LastModified
		);
	}

	function setPrimaryEntityName(reportValues) {
		reportValues = reportValues ?? ctrl.reportMetadata.report.Fields ?? [];

		const primaryEntityFieldConfig = ctrl.form.Fields.find(f => f.IsPrimary === true && f.Type.IsEntity === true);
		const primaryEntityValue = reportValues.find(v => v.FieldId === primaryEntityFieldConfig.Id);
		if(primaryEntityValue?.EntityId === undefined) {
			return "";
		}

		const primaryEntity = entityManager.findFriendlyByPrimaryId(primaryEntityFieldConfig.Data, primaryEntityValue.EntityId);

		return primaryEntity.Name;
	}

	function groupReportValues(values) {
		const report = values === undefined ? ctrl.reportMetadata.report : { ...ctrl.reportMetadata.report, Fields: values };
		const reportValues = {
			[headerValuesKey]: [],
			[detailValuesKey]: {}
		};

		if(report?.Fields !== undefined) {
			const isReportCopy = ctrl.report !== undefined && ctrl.report.Id === undefined;
			const reportDetailValues = report.Fields.filter(v => v.DetailId !== undefined);
			reportValues[headerValuesKey] = report.Fields.filter(v => v.DetailId === undefined);

			if(isReportCopy) {
				shouldLogDirtyChanges() && $logger.debug("report copy detected, marking all header fields as dirty", { reportValues: reportValues[headerValuesKey] });
				reportValues[headerValuesKey].forEach(v => ctrl.dirtyHeaderFields[v.FieldId] = true);
			}

			const detailsById = groupBy(reportDetailValues, v => v.DetailId);
			const detailValues = {};

			forEach(detailsById, (vals, detailIdKey) => {
				const detailId = parseInt(detailIdKey);

				if(isReportCopy) {
					ctrl.dirtyDetailTracking[detailId] = true;
				}

				detailValues[detailId] = {
					detailId,
					values: vals,
					isDeleted: some(vals, v => v.IsDeleted),
					isNewDetail: isReportCopy
				};
			});
			reportValues[detailValuesKey] = detailValues;
		}

		return reportValues;
	}

	function registerEvents() {
		const buildReportOpenedEvent = $logger.buildReportEvent("open");
		const primaryEntityFieldConfig = ctrl.form.Fields.find(f => f.IsPrimary === true && f.Type.IsEntity === true);

		ctrl.eventBus.register(EVENT_VALUE_CHANGED(primaryEntityFieldConfig.Id), (_, { value }) => {
			const fieldValue = {
				FieldId: primaryEntityFieldConfig.Id,
				EntityId: value?.Id
			};

			ctrl.primaryEntityName = setPrimaryEntityName([fieldValue]);
		}, ctrl.scope);

		ctrl.eventBus.register(EVENT_REPORT_STATUS_CHANGE, (_, report) => updateRuleMetadata(report), ctrl.scope);

		ctrl.eventBus.register(
			EVENT_PAGE_LOADED,
			() => {
				if(!ctrl.reportMetadata.isNew) {
					const headerFields = ctrl.form.Fields.filter(f => f.IsDetail !== true);
					triggerFilters(headerFields);
					triggerAreaFilters();

					if(ctrl.currentHistory?.State.Code !== "SUBMITTED") {
						triggerPrimaryDateFilter(undefined, false);
					}

					const uiValidations = ctrl.suppressErrors ? [] : ctrl.reportRules;

					ctrl.validationService.updateApiValidations(uiValidations);
					ctrl.validationService.commitValidations();
					setReportState();

					ctrl.pageIsLoaded = true;
					ctrl.eventBus.trigger(EVENT_PROCESS_UI_RULES, {});
					disableLockOnDataFields();

					$logger.event(buildReportOpenedEvent("edited"), { report: ctrl.reportMetadata });
				} else {
					ctrl.pageIsLoaded = true;
					triggerDefaultFieldValues();
					ctrl.eventBus.trigger(EVENT_PROCESS_UI_RULES, {});

					const scopes = [REPORT_SCOPES.report];

					const values = ctrl.ruleProvider.getValuesByType(scopes);
					triggerFilters(undefined, values[REPORT_SCOPES.report]);
					$logger.event(buildReportOpenedEvent("started"));
				}
			},
			ctrl.scope
		);

		ctrl.eventBus.register(
			EVENT_REPORT_UNLOCKED,
			() => {
				setReportReadonlyStatus();

				if(!ctrl.form.readonly) {
					setFieldOptions(ctrl.form.Fields);
					disableLockOnDataFields();
				}
			},
			ctrl.scope
		);

		ctrl.eventBus.register(
			EVENT_REPORT_LOCKED,
			() => {
				ctrl.eventBus.trigger(EVENT_SET_FORM_PRISTINE);
			},
			ctrl.scope
		);

		ctrl.eventBus.register(
			EVENT_DETAILS_SUPPRESSION_TOGGLED,
			(e, params) => {
				setDetailsSuppressed(params.suppressed);
			},
			ctrl.scope
		);

		ctrl.eventBus.register(
			EVENT_DETAIL_DIRTY,
			(e, params) => {
				ctrl.dirtyDetailTracking[params.detailId] = true;
				updateReportDirty();
			},
			ctrl.scope
		);

		ctrl.eventBus.register(
			EVENT_DETAIL_TOGGLE,
			(e, params) => {
				if(ctrl.dirtyDetailTracking[params.detailId]) {
					delete ctrl.dirtyDetailTracking[params.detailId];
				}
				else {
					ctrl.dirtyDetailTracking[params.detailId] = true;
				}
				updateReportDirty();
				disableLockOnDataFields();
				runDetailsRules(undefined, params.detailId).then(triggeredRules => {
					ctrl.validationService.updateTriggeredRules(triggeredRules, [ctrl.reportScopes.details, ctrl.reportScopes.detail, ctrl.reportScopes.reportAndDetails], params.detailId);
					ctrl.validationService.commitValidations();
					setReportState();

					ctrl.eventBus.trigger(EVENT_PROCESS_UI_RULES, {});
				});
			},
			ctrl.scope
		);

		ctrl.eventBus.register(
			EVENT_SET_FORM_PRISTINE,
			() => {
				ctrl.dirtyDetailTracking = {};
				ctrl.dirtyHeaderFields = {};
				shouldLogDirtyChanges() && $logger.debug("report form is pristine, clearing dirty tracking", {
					dirtyHeaderFields: ctrl.dirtyHeaderFields,
					dirtyDetailTracking: ctrl.dirtyDetailTracking
				});

				updateReportDirty();
			},
			ctrl.scope
		);

		$scope.$watch(
			() => ctrl.dirtyHeaderFields,
			() => {
				if(shouldLogDirtyChanges()) {
					const dirtyFields = ctrl.form.Fields.filter(f => ctrl.dirtyHeaderFields[f.Id] === true);
					$logger.debug("header fields changed, updating report dirty state", { dirtyHeaderFields: dirtyFields });
				}
				updateReportDirty();
			}
		);

		if(!ctrl.isMinimalMode()) {
			window.addEventListener("beforeunload", ctrl.alertUserIfDirty);
			listenForExit();
		}
	}

	ctrl.enableAddDetail = enable => {
		ctrl.showAddDetail = enable;
	};

	function initLocks(fields) {
		forEach(fields, f => f.isFieldLockable = f.IsLockable);
	}

	function triggerDefaultFieldValues() {
		const defaultValueFields = ctrl.form.Fields.filter(f => f.IsFieldDefaultEnabled === true);
		defaultValueFields.forEach(f => {
			ctrl.eventBus.trigger(EVENT_SET_DEFAULT_VALUE(f.Id));
		});
	}

	function listenForExit() {
		const isNewReportToEditReport = (transition) => {
			const isThisNewReport = transition.from().name === NEW_REPORT;
			const isGoingToEditReport = transition.to().name === EDIT_REPORT;
			const fromHasReportId = !!transition.params("from").id;
			const toHasReportId = !!transition.params("to").id;
			const wasNewReport = transition.options().custom?.isNewReport === true;

			return isThisNewReport && isGoingToEditReport && !fromHasReportId && toHasReportId && wasNewReport;
		};

		ctrl.deregisterTransitionHook = $transitions.onExit(
			{
				from: state => {
					return state.name === NEW_REPORT || state.name === EDIT_REPORT;
				}
			},
			async(transition, state) => {
				if(state.abstract !== true) {
					if(ctrl.isReportDirty && ctrl.currentHistory?.State.Code !== "DELETED") {
						$logger.event(unsavedChangesEvent, { report: ctrl.reportMetadata });

						if(!confirm("You have unsaved changes.  Are you sure you want to leave this page?")) {
							return false;
						}
					}

					if(!isNewReportToEditReport(transition)) {
						const shouldProceed = await confirmCompletionBeforeNavigation();
						if(!shouldProceed) {
							return false;
						}
					}
				}

				return true;
			}
		);
	}

	async function confirmCompletionBeforeNavigation() {
		if(!shouldPromptForCompletionOnLeave()) {
			return true;
		}

		const result = await confirmCompletionOnLeave();

		if(result.action === "leave") {
			return true;
		}

		if(result.action === "complete") {
			ctrl.handleSubmit();
		}

		return false;
	}

	function shouldPromptForCompletionOnLeave() {
		const report = ctrl.reportMetadata?.report;
		const isExistingReport = !ctrl.reportMetadata?.isNew;
		const canAmendOrNeverSubmitted = ctrl.permissions.Actions.CanAmend || !hasHadState(REPORT_STATES.submitted.code, report);

		const shouldPrompt = !ctrl.isReportDirty
			&& !ctrl.isProcessingRules
			&& !ctrl.isProcessingQueueUnsettled
			&& isExistingReport
			&& canDisplayCompleteActions()
			&& canAmendOrNeverSubmitted
			&& !hasBlockingCompletionValidations();

		return shouldPrompt;
	}

	function hasBlockingCompletionValidations() {
		return [TRIGGER_OPERATOR_CODES.blockSave, TRIGGER_OPERATOR_CODES.blockSubmit]
			.some(validationType => {
				const validations = ctrl.validationService?.getRuleValidationsOfType(validationType);
				return (validations?.rules?.length ?? 0) > 0;
			});
	}

	function isCompletionUnavailableState() {
		const currentStateCode = ctrl.currentHistory?.State?.Code;

		return currentStateCode === REPORT_STATES.submitted.code
			|| currentStateCode === "SUBMITTING"
			|| currentStateCode === "DELETED";
	}

	function canDisplayCompleteActions() {
		return !!ctrl.form
			&& !isCompletionUnavailableState()
			&& !ctrl.form.readonly
			&& !ctrl.permissions?.IsDisabled
			&& !!ctrl.permissions?.Actions?.CanSubmit;
	}

	function updateReportDirty() {
		ctrl.isReportDirty = size(ctrl.dirtyHeaderFields) > 0 || size(ctrl.dirtyDetailTracking) > 0;
		shouldLogDirtyChanges()
			&& $logger.debug("report dirty state updated", {
				isReportDirty: ctrl.isReportDirty,
				dirtyHeaderFields: ctrl.dirtyHeaderFields,
				irtyDetailTracking: ctrl.dirtyDetailTracking
			});

		if(ctrl.isMinimalMode()) {
			return;
		}

		if(ctrl.reportMetadata.isNew) {
			$state.current.title = `New ${ctrl.form.nomenclature.PositiveReport}`;
		}
		else if(ctrl.isReportDirty) {
			$state.current.title = `*Edit ${ctrl.form.nomenclature.PositiveReport} #${ctrl.reportMetadata.report.Number}`;
		}
		else {
			$state.current.title = `Edit ${ctrl.form.nomenclature.PositiveReport} #${ctrl.reportMetadata.report.Number}`;
		}
	}

	function setModifiedDates(show = false) {
		if(show) {
			ctrl.suppressErrors = false;
		} else {
			ctrl.suppressErrors = !!ctrl.reportMetadata.report.Created
				&& !!ctrl.reportMetadata.report.LastModified
				&& moment(ctrl.reportMetadata.report.Created).isSame(moment(ctrl.reportMetadata.report.LastModified), "second");
		}

		ctrl.modifiedDates = { Created: ctrl.reportMetadata.report.Created, LastModified: ctrl.reportMetadata.report.LastModified };
	}

	function disableLockOnDataFields() {
		const undeletedDetailsNested = filter(ctrl.reportValues[detailValuesKey], (val) => !val.isDeleted)
			.map(detail => map(detail.values));

		const undeletedDetails = reduce(undeletedDetailsNested, (acc, curr) => {
			return acc.concat(curr);
		}, []);

		const lockOnDetailDataFields = ctrl.form.reportFields.filter(f => f.lockOnDetailData);
		forEach(lockOnDetailDataFields, f => {
			if(undeletedDetails && undeletedDetails.length > 0) {
				f.isReadonly = true;
			} else {
				f.isReadonly = ctrl.form.readonly;
			}
		});
	}

	function setReportReadonlyStatus() {
		//isReadonly -> field cannot be edited in form
		//isDisabled -> field needs something else to happen on the form before it can be modified

		ctrl.form.readonly =
			(ctrl.reportScope.isReadOnly ?? false) ||
			ctrl.currentHistory?.State.Code === "DELETED" ||
			ctrl.currentHistory?.State.Code === "SUBMITTED" ||
			ctrl.permissions.IsDisabled ||
			!ctrl.permissions.Actions.CanEdit ||
			(!ctrl.permissions.Actions.CanAmend && hasHadState(REPORT_STATES.submitted.code, ctrl.reportMetadata.report));
	}

	function setDetailsSuppressed(isSuppressed) {
		ctrl.suppressDetails = isSuppressed;
	}

	function setupTimezoneOverride(fields) {
		const dateFields = fields.filter(f =>
			f.Template === FIELD_TEMPLATES.DateTime
			|| f.Template === FIELD_TEMPLATES.Date
			|| f.Template === FIELD_TEMPLATES.YearMonth
		);

		if(!dateFields || dateFields.length === 0) {
			return;
		}

		const registerDateTimeFieldsForTZAbbreviation = (timezoneCode) => {
			const timezoneAbbr = find(TIME_ZONES, z => z.ianaCode === timezoneCode);
			dateFields.forEach(f => {
				f.timezone = timezoneCode;
				if(f.Template === FIELD_TEMPLATES.DateTime) {
					ctrl.eventBus.register(
						EVENT_VALUE_CHANGED(f.Id),
						(e, params) => {
							f.tzAbbr = timezoneAbbr.code(params.value.date);
						},
						$scope
					);
				}
			});
		};

		if(ctrl.form.TimeMode.Code === FORM_TIMEZONE_TYPES.ByUser) {
			// TODO:
			// registerDateTimeFieldsForTZAbbreviation(*user*.TimeZone.Code);
			// if(ctrl.reportMetadata.report.TimeZone && ctrl.reportMetadata.report.TimeZone.Code !== *user*.TimeZone.Code) {
			//	notifyUserTimezoneError();
			// 	throw new Error();
			// }

			notifyUserTimezoneError();
			throw new Error("Not implemented by-user timemode for resolution");
		}

		if(ctrl.form.TimeMode.Code === FORM_TIMEZONE_TYPES.ByForm) {
			registerDateTimeFieldsForTZAbbreviation(ctrl.form.TimeZone.Code);
			if(ctrl.reportMetadata.report.TimeZone && ctrl.reportMetadata.report.TimeZone.Code !== ctrl.form.TimeZone.Code) {
				notifyFormTimezoneError(ctrl.reportMetadata.report.TimeZone.Code, ctrl.form.TimeZone.Code);

				throw new Error("Unexpected time mode form in timezone resolution");
			}
			return;
		}

		const primaryEntityField = find(fields, f => f.IsPrimary && (f.Type.Code === "ENTITY" || f.Type.Code === "ENTITYCONTACT"));
		primaryEntityField.setEntityTimezone = true;

		if(ctrl.reportMetadata.report.TimeZone) {
			const thisReportEntityValue = ctrl.reportMetadata.report.Fields.find(f => f.FieldId === primaryEntityField.Id);
			const thisReportEntityTimezoneCode = entityManager.getUniqueTimezoneForEntity(primaryEntityField.Id, thisReportEntityValue.EntityId);

			if(thisReportEntityTimezoneCode === UNIQUE_TIMEZONE_ERRORS.MULTIPLE) {
				notifyMultipleEntityTimezoneError(primaryEntityField.Name);
				throw new Error("Selected entity has multiple available timezones in ByEntity configuration");
			}
			else if(thisReportEntityTimezoneCode === UNIQUE_TIMEZONE_ERRORS.ZERO) {
				notifyNoEntityTimezoneError(primaryEntityField.Name);
				throw new Error("Selected entity has no available timezones in ByEntity configuration");
			}

			if(thisReportEntityTimezoneCode !== ctrl.reportMetadata.report.TimeZone.Code) {
				notifyTimezoneDiscrepancy(ctrl.reportMetadata.report.TimeZone.Code, thisReportEntityTimezoneCode, primaryEntityField.Name);
			}
		}

		dateFields.forEach(field => {
			let timezoneAbbr;
			let lastVal;

			field.needsTimezone = true;

			ctrl.eventBus.register(
				EVENT_TIMEZONE_SET(primaryEntityField.Id),
				(e, params) => {
					field.timezone = params.value;

					if(field.Template === FIELD_TEMPLATES.DateTime) {
						timezoneAbbr = find(TIME_ZONES, z => z.ianaCode === params.value);
						field.tzAbbr = timezoneAbbr.code(lastVal);
					}
				},
				$scope
			);

			if(field.Template === FIELD_TEMPLATES.DateTime) {
				ctrl.eventBus.register(
					EVENT_VALUE_CHANGED(field.Id),
					(e, params) => {
						lastVal = params.value.date;
						if(timezoneAbbr) {
							field.tzAbbr = timezoneAbbr.code(lastVal);
						}
					},
					$scope
				);
			}
		});
	}

	function setFieldOptions(fields) {
		setReportReadonlyStatus();
		fields.forEach(field => {
			if(field.Template === FIELD_TEMPLATES.Number) {
				const isSimpleRangeRule = (rule) => some(rule.appliedFieldIds, y => y === field.Id)
					&& rule.Conditions !== undefined
					&& rule.Conditions.length === 1
					&& rule.Engine !== undefined
					&& rule.Engine.Code === ENGINE_EVALUATIONS.range.code;

				const rangeRule = filter(
					ctrl.form.Rules,
					r => {
						const hasChildRangeRule = () => some(r.appliedFieldIds, y => y === field.Id)
							&& r.Operator
							&& r.Operator.Code === RULE_OPERATORS.OR
							&& r.Children
							&& r.Children.length > 0
							&& filter(r.Children, c => isSimpleRangeRule(c)).length === 1;

						const srr = isSimpleRangeRule(r);
						const hcrr = hasChildRangeRule();

						return srr || hcrr;
					}
				)[0];

				if(rangeRule?.Children !== undefined) {
					const range = find(rangeRule.Children, y =>
						y.Children === undefined && y.Engine.Code === ENGINE_EVALUATIONS.range.code
					);

					if(!!range && some(rangeRule.Children, y => y.Children === undefined && y.Engine.Code === ENGINE_EVALUATIONS.empty.code)) {
						field.MaxValue = range.Conditions[0].MaxValue;
						field.MinValue = range.Conditions[0].MinValue;
					}
				}
				else if(rangeRule?.Conditions !== undefined) {
					field.MaxValue = rangeRule.Conditions[0].MaxValue;
					field.MinValue = rangeRule.Conditions[0].MinValue;
				}
			}

			field.isTiered = (field.Type.Code === "ENTITY" || field.Type.Code === "ENTITYCONTACT") && !field.isSelf;
			setFieldSize(field);

			if(!field.isTiered && field.Template === FIELD_TEMPLATES.EntitySelector) field.Template = FIELD_TEMPLATES.Selection;

			if(field.Template === "TextArea") {
				field.MaxValue = 300;
			}

			field.isRequired = some(ctrl.form.Rules, r => r.Engine && r.Engine.Code === ENGINE_EVALUATIONS.required.code && some(r.Conditions, c => c.Field.Id === field.Id));

			field.hasEffectiveRanges = field.Type.IsEntity && some(field.Data, data => {
				return data.Effective?.length > 0
					|| (
						data.Entities
						&& some(data.Entities, e => e.Entity.Effective?.length > 0)
					);
			});

			if(ctrl.form.readonly) {
				field.isReadonly = true;
			} else {
				if(ctrl.reportMetadata.isNew || field.IsDetail) {
					field.isReadonly = false;
					initializeNewField(field, fields);
				}
				else if(field.ReadOnlyMode === READ_ONLY_MODES.afterCreate) {
					field.isReadonly = true;
				}
				else {
					const fieldValue = find(ctrl.reportValues[headerValuesKey], f => f.FieldId === field.Id);
					if(field.ReadOnlyMode === READ_ONLY_MODES.afterFirstSet && !!fieldValue) {
						field.isReadonly = true;
					} else {
						field.isReadonly = false;
						disableLockOnDataFields();

						if(field.Template !== FIELD_TEMPLATES.Text || !field.Data || field.Data.length === 0) {
							field.isDisabled = fieldValue && fieldValue.IsNotAvailable;
						}
					}
				}

				if(field.ReadOnlyMode === READ_ONLY_MODES.system || field.ReadOnlyMode === READ_ONLY_MODES.always) {
					field.isReadonly = true;
				}
			}
		});
	}

	function initializeNewField(field) {
		if(!field.isLocked) {
			ctrl.eventBus.trigger(EVENT_CLEAR_VALUE(field.Id), { isInitializing: true });
			field.hasValidationError = false;
			field.hasValidationWarning = false;
			field.validationErrorRepeatIds = [];
			field.validationWarningRepeatIds = [];

			const parentFields = filter(
				field.features,
				f => f.type === FEATURE_TYPES.filter && f.triggerFieldId && f.triggerFieldId !== field.Id && !find(ctrl.form.Fields, fi => fi.Id === f.triggerFieldId).isLocked
			);

			field.isDisabled = field.hasEffectiveRanges || !!parentFields.length || some(field.features, f => f.Type === FEATURE_TYPES.mapPoint && f.geoJsons === undefined);
			field.isLocked = field.IsSticky;

			if(field.hasEffectiveRanges) {
				field.placeHolder = `Please select a ${ctrl.form.nomenclature.PrimaryDate}`;
				if(field.IsDetail) {
					registerDetailFieldForPrimaryDate(field);
				}
			}

			if(parentFields.length) {
				const names = parentFields.map(pf => {
					return (find(ctrl.form.Fields, fi => fi.Id === pf.triggerFieldId) || {}).Name;
				});
				field.placeHolder = `Please select ${names.join(",")}`;
			}
		}
	}

	function registerDetailFieldForPrimaryDate(field) {
		ctrl.eventBus.register(
			EVENT_FILTER_PRIMARYDATE,
			(payload, params) => {
				field.effectiveAsOf = params.effectiveAsOf;
				field.isDisabled = false;
			},
			$scope
		);
	}

	function setFieldSize(field) {
		let size = "col-md-4 col-sm-6 col-print-4";

		if(field.IsSmall) {
			size = "col-md-2 col-sm-3 col-print-2";
		}
		else if(field.IsFullWidth || field.Template === FIELD_TEMPLATES.Markdown || field.Template === FIELD_TEMPLATES.StringMulti) {
			size = "col-md-12 col-sm-12 col-print-12";
		}

		if(field.Template === FIELD_TEMPLATES.EntityContactSelector) {
			size = "col-md-8 col-sm-12 col-print-8";
		}

		field.size = size;
	}

	function initializeFeatures(form) {
		form.Features.forEach(feat => {
			if(feat.Type === FEATURE_TYPES.mapPoint) {
				featureHelper.mapPoint(feat, form.Fields, $scope, ctrl.eventBus);
			} else {
				const mapRelatedFieldIds = feat.Type === FEATURE_TYPES.map
					? feat.Fields.reduce((ids, entry) => {
						ids.push(entry.Field.Id);
						return ids;
					}, [])
					: [];

				feat.Fields.forEach(featureField => {
					const field = find(form.Fields, f => featureField.Field.Id == f.Id);
					const trigger = find(feat.Fields, { Order: featureField.Order - 1 }) || { Field: {} };

					const featureProperties = {
						type: feat.Type,
						isInitiator: some(feat.Fields, x => x.Order > featureField.Order),
						fieldFeature: featureField,
						triggerFieldId: trigger.Field.Id
					};

					if(featureProperties.triggerFieldId && feat.Type === FEATURE_TYPES.filter) {
						registerFilterEvent(feat, featureField, featureProperties.triggerFieldId);
						if(!field.Pivot || field.Pivot !== PIVOT_TYPES.Repeat) {
							field.triggerFieldId = trigger.Field.Id;
						}

						const triggerField = find(form.Fields, f => featureProperties.triggerFieldId == f.Id);
						if(triggerField.Pivot && triggerField.Pivot !== PIVOT_TYPES.Repeat) {
							forEach(triggerField.Data, dataItem => {
								registerFilterEvent(feat, featureField, featureProperties.triggerFieldId, dataItem.Id);
							});
						}

						if(!triggerField.IsDetail && !!field.IsDetail) {
							triggerField.lockOnDetailData = true;
						}
					} else if(feat.Type === FEATURE_TYPES.map) {
						featureProperties.relatedFieldIds = mapRelatedFieldIds;
						featureProperties.data = (feat.data || []).reduce((fields, mappedField) => {
							if(some(mapRelatedFieldIds, id => id === mappedField.Id)) {
								fields.push(mappedField);
							}

							return fields;
						}, []);
					} else if(feat.Type === FEATURE_TYPES.propagate && featureProperties.triggerFieldId) {
						ctrl.eventBus.register(
							EVENT_VALUE_CHANGED(featureProperties.triggerFieldId),
							(e, params) => {
								const currentValues = getAllValues(true);
								const thisCurrentValue = currentValues.find(v => v.FieldId === field.Id);

								if(thisCurrentValue === undefined) {
									ctrl.eventBus.trigger(EVENT_SET_VALUE(field.Id), { value: params.value });
								}
							},
							$scope
						);
					}

					if(!field.features) {
						field.features = [];
					}

					field.features.push(featureProperties);
				});
			}
		});
	}

	function registerFilterEvent(feature, featureField, triggerFieldId, repeatId) {
		ctrl.eventBus.register(EVENT_VALUE_CHANGED(triggerFieldId, repeatId), (e, params) => handleFilterEvent(params, triggerFieldId, feature, featureField, repeatId), $scope);
		ctrl.eventBus.register(EVENT_ENABLE_SUCCESSOR_LOCK(triggerFieldId, undefined), () => {
			ctrl.eventBus.trigger(EVENT_HANDLE_SUCCESSOR_LOCK(featureField.Field.Id), { enable: true });
		}, $scope);
		ctrl.eventBus.register(EVENT_DISABLE_SUCCESSOR_LOCK(triggerFieldId, undefined), () => {
			ctrl.eventBus.trigger(EVENT_HANDLE_SUCCESSOR_LOCK(featureField.Field.Id), { enable: false });
		}, $scope);
	}

	function handleFilterEvent(params, triggerFieldId, feature, featureField, repeatId) {
		const selectedField = find(feature.Fields, fi => fi.Field.Id === params.field.Id);

		if(params.refilterThis === true) {
			const previousField = find(feature.Fields, fi => fi.Order === selectedField.Order - 1);
			executeFilter(previousField, selectedField);
		}

		if(!params.value) {
			ctrl.eventBus.trigger(EVENT_FILTER_CURRENT_OPTIONS(featureField.Field.Id), { clear: true, readonly: true });
			return;
		}

		selectedField.value = params.value;

		executeFilter(selectedField, featureField);

		function executeFilter(filterField, thisField) {
			const selectedFields = filter(feature.Fields, d => d.value && d.Order <= filterField.Order);
			const results = orderBy(selectedFields, d => d.Order).map(i => {
				if(Array.isArray(i.value)) {
					return i.value.join();
				}
				return i.value.Id;
			});
			const searchBy = results.join(",") + ",";
			const filtered = filter(feature.data, d => {
				return startsWith(d, searchBy);
			});
			const nextIds = compact(
				filtered.map(f => {
					const truncatedData = f.substring(searchBy.length, f.length);
					const end = truncatedData.indexOf(",");

					const endData = end === -1 ? truncatedData : truncatedData.substring(0, end);

					if(endData.trim() === IS_NOT_AVAILABLE_VALUE) {
						//NOTE: endData ends in an invisible newline character.  Don't use that.
						return IS_NOT_AVAILABLE_VALUE;
					}

					return parseInt(end === -1 ? truncatedData : truncatedData.substring(0, end));
				})
			);
			const distinctIds = uniq(nextIds);

			ctrl.eventBus.trigger(EVENT_FILTER_CURRENT_OPTIONS(thisField.Field.Id, repeatId), { ids: distinctIds, isFromInitialValue: params.initialValue, forceTrigger: true });
		}
	}

	function onDestroy() {
		ctrl.deregisterTransitionHook();
		ctrl._unsubscribe();
		ctrl.detailModalInstance && ctrl.detailModalInstance.dismiss("instance dismiss");
		window.removeEventListener("beforeunload", ctrl.alertUserIfDirty);
	}

	function setHotKeys() {
		const hasLockableFields = some(ctrl.form.reportFields, f => f.IsLockable);

		if(hasLockableFields) {
			hotkeys.bindTo($scope).add(
				hotkeyConfigs.EDIT_REPORT.DETAILS.LOCK_FIELD(() => {
					ctrl.eventBus.trigger(EVENT_HOTKEY_LOCK);
				})
			);
		}

		hotkeys.bindTo($scope).add(
			hotkeyConfigs.EDIT_REPORT.ADD_DETAIL(() => {
				ctrl.editDetail();
			})
		);

		if(ctrl.scope && ctrl.scope.getForm) {
			hotkeys.bindTo($scope).add(
				hotkeyConfigs.EDIT_REPORT.SAVE_HEADER(() => {
					ctrl.scope.getForm().$submit();
				})
			);
		}
	}

	function setCallbacks(callbacks) {
		ctrl.setValidationsForDetails = validations => callbacks.setValidations(validations);
	}

	function blockSave(rules) {
		if((rules || {}).length === 0) {
			return false;
		}

		const nonUniqueRules = some(
			rules,
			r =>
				(r.Engine || {}).Code !== "UNIQUE" ||
				(r.Engine || {}).Code !== "INTERSECT" ||
				(r.Engine || {}).Code !== "EXISTING" ||
				some(r.Children, c => c.Engine.Code !== "UNIQUE" || c.Engine.Code !== "INTERSECT" || c.Engine.Code !== "EXISTING")
		);

		return nonUniqueRules;
	}

	const propertiesToStrip = [
		"repeatId",
		"isDirty",
		"_code",
		"Classifications",
		"Identifiers",
		"Seasons",
		"Order",
		"DeactivatedAt",
		"factor",
		"factoredValue",
		"hasResultForRuleRun",
		"hasFullResult",
		"allCodes",
		"externalKeys"
	];

	const whitelistedProperties = [
		"ParentDetailLinkKey",
		"DetailLinkKey",
		"FieldId",
		"DataId",
		"EntityId",
		"Number",
		"Date",
		"Time",
		"isDeleted",
		"IsDeleted",
		"DetailId",
		"IsNotAvailable",
		"String",
		"File",
		"ContactId",
		"Latitude",
		"Longitude",
		"Boolean",
		"$metadata",
		"Ordinal"
	];

	const cleanValue = (value) => {
		const returnVal = {
			...value
		};

		if(!!returnVal.Detail) {
			delete returnVal.Detail;
		}

		propertiesToStrip.forEach(p => {
			if(Object.prototype.hasOwnProperty.call(returnVal, p)) {
				delete returnVal[p];
			}
		});

		const nonWhitelistedPropertiesRemaining = Object.keys(returnVal).filter(k => whitelistedProperties.indexOf(k) === -1);

		return { returnVal, nonWhitelistedPropertiesRemaining };
	};

	function getAllValues(isApi = false) {
		let headerVals = ctrl.getFormValues(isApi);
		if(headerVals == null) {
			throw new Error("Invalid field value discovered");
		}
		const allNonWhitelistedProperties = [];

		if(isApi) {
			headerVals = headerVals.map(v => {
				const { returnVal, nonWhitelistedPropertiesRemaining } = cleanValue(v);
				if(nonWhitelistedPropertiesRemaining?.length) {
					allNonWhitelistedProperties.push(...nonWhitelistedPropertiesRemaining);
				}

				return returnVal;
			});
		}

		if(ctrl.suppressDetails) {
			return headerVals;
		}

		const detailValArray = flatten(map(ctrl.reportValues[detailValuesKey], d => {
			return Object.values(d.values)
				.map(v => {
					if(!isApi) {
						return {
							...v
						};
					}

					const { returnVal, nonWhitelistedPropertiesRemaining } = cleanValue(v);
					if(nonWhitelistedPropertiesRemaining?.length) {
						allNonWhitelistedProperties.push(...nonWhitelistedPropertiesRemaining);
					}
					if(d.isNewDetail) {
						delete returnVal.DetailId;
					}

					return returnVal;
				})
				.filter(returnVal => {
					const valueConfig = ctrl.form.detailFields.find(f => f.Id === returnVal.FieldId);
					if(
						valueConfig?.Type.Code === "NUMBER"
						&& (
							!Object.prototype.hasOwnProperty.call(returnVal, "Number")
							&& !Object.prototype.hasOwnProperty.call(returnVal, "IsNotAvailable")
						)
					) {
						return false;
					}
					return returnVal.FieldId > -1
						&& returnVal.isDeleted !== true;
				});
		}));

		if(allNonWhitelistedProperties.length > 0) {
			const uniqueNonWhitelistedProperties = uniq(allNonWhitelistedProperties);

			$logger.warn(
				"Detected non-whitelisted properties being allowed through field value cleanup",
				{
					properties: uniqueNonWhitelistedProperties
				}
			);
		}

		return headerVals.concat(detailValArray);
	}

	async function saveReport() {
		if(ctrl.isMinimalMode()) {
			return;
		}

		if(ctrl.reportMetadata.isNew && !!appState.invalidTimeoutAt && appState.invalidTimeoutAt <= new Date()) {
			showForceRefresh();
			return;
		}

		const isNewReport = ctrl.reportMetadata.isNew;
		const allValues = getAllValues(true);
		triggerPrimaryDateFilter(allValues);
		const report = await handleSave(getAllValues(true));
		if(!report) {
			return;
		}

		return {
			report,
			isNewReport
		};
	}

	async function handleSaveReport() {
		const result = await saveReport();

		if(result?.isNewReport) {
			transitionToEditReport(result.report, { isNewReport: true });
		}

		return result?.report;
	}

	async function materializeAsyncValues(values) {
		const promises = values.map(async v => {
			try {
				if(v.$metadata?.$materialize) {
					const materialized = await Promise.resolve(v.$metadata.$materialize());

					if(Array.isArray(materialized)) {
						return materialized.map(item => {
							if(item?.$metadata) {
								delete item.$metadata;
							}
							return item;
						});
					}

					if(materialized?.$metadata) {
						delete materialized.$metadata;
					}

					return [materialized];
				}
			}
			catch(error) {
				$logger.event(reportSaveEventBuilder("materializeErrored"), {
					report: ctrl.reportMetadata,
					fieldValue: v
				});

				toastr.error("There was an operation failure preventing you from saving this report.");

				throw error;
			}

			return [v];
		});

		const asyncValues = await Promise.all(promises);

		return asyncValues.flat();
	}

	async function handleSave(updates) {
		if(ctrl.isMinimalMode()) {
			return;
		}

		const scopes = [ctrl.reportScopes.report, ctrl.reportScopes.details, ctrl.reportScopes.reportAndDetails, ctrl.reportScopes.metadata];

		if(ctrl.suppressDetails) {
			ctrl.reportValues[detailValuesKey] = {}; // wipe here in order to avoid deleting details in an intermediary state
		}

		const rules = await ctrl.ruleProvider.validate(scopes);
		$logger.event(reportSaveEventBuilder("requested"), {
			report: ctrl.reportMetadata,
			rules
		});

		ctrl.validationService.updateTriggeredRules(rules, scopes);
		ctrl.validationService.commitValidations();
		setReportState();

		const type = ctrl.validationService.getRuleValidationsOfType(TRIGGER_OPERATOR_CODES.blockSave);

		if(blockSave(type.rules)) {
			$logger.event(reportSaveEventBuilder("blocked"), {
				report: ctrl.reportMetadata,
				rules
			});

			toastr.error("There are errors preventing you from saving this data.");
			return;
		}

		const isNewReport = ctrl.reportMetadata.isNew;
		const initialValues = isNewReport ? [] : snapshotUsageUpdates(ctrl.reportMetadata.report?.Fields);
		let materialized = updates;

		try {
			const materializing = materializeAsyncValues(updates);
			materialized = await loadingService.watch(materializing);
		}
		catch(err) {
			return;
		}

		try {
			const resp = await ctrl.reportScope.actions.saveReport(materialized, ctrl.reportMetadata.report);
			$logger.event(reportSaveEventBuilder("succeeded"), {
				"form-id": ctrl.form.Id,
				"report-id": resp.Id,
				isNewReport
			});

			setReportMetadata(resp);

			setApiValidations(resp.Rules, isNewReport);
			ctrl.validationService.commitValidations();
			setReportState();

			setFormUpdates(resp, materialized, initialValues);
			setModifiedDates(isNewReport);

			if(isNewReport) {
				ctrl.dirtyDetailTracking = {};
				ctrl.dirtyHeaderFields = {};
				shouldLogDirtyChanges() && $logger.debug("report saved, clearing dirty tracking", { dirtyHeaderFields: ctrl.dirtyHeaderFields, dirtyDetailTracking: ctrl.dirtyDetailTracking });
				ctrl.isReportDirty = false;
			}

			const readOnlyModeFields = ctrl.form.Fields.filter(f => !!f.ReadOnlyMode);
			forEach(readOnlyModeFields, f => {
				if(f.ReadOnlyMode === READ_ONLY_MODES.afterCreate) {
					f.isReadonly = true;
				} else {
					const fieldVal = find(ctrl.reportMetadata.report.Fields, v => v.FieldId === f.Id);
					if(!!fieldVal) {
						f.isReadonly = true;
					}
				}
			});

			toastr.success(`${ctrl.form.nomenclature.PositiveReport} saved.`);

			ctrl.eventBus.trigger(EVENT_REPORT_SAVED);
			ctrl.eventBus.trigger(EVENT_SET_FORM_PRISTINE);

			return resp;
		}
		catch(resp) {
			ctrl.reportMetadata.report.hasErrors = true;
			if(resp.Data?.Rules) {
				resp.Data.Rules.forEach(r => {
					if(r.Detail && r.Detail.Id === undefined && r.Detail.LinkKey !== undefined) {
						const matchingVal = Object.values(ctrl.reportValues[detailValuesKey])
							.find(detail => Object.values(detail.values).some(v => v.DetailLinkKey === r.Detail.LinkKey));
						r.Detail.Id = matchingVal.detailId;
					}
				});

				setApiValidations(resp.Data.Rules, isNewReport);
				ctrl.validationService.commitValidations();
				setReportState();

				$logger.event(reportSaveEventBuilder("failed"), {
					serverRules: resp.Data.Rules
				});
			}

			$logger.event(reportSaveEventBuilder("serverErrored"));

			throw new Error("SAVE FAILED");
		}
	}

	function handleDetailSave(saveValues) {
		$logger.event($logger.reportDetailEventBuilder("completed"), { saveValues });
		const newDetails = { ...saveValues };
		const headerVals = ctrl.getFormValues();
		let hasBlockSave = false;

		ctrl.validationService.clearPendingValidations();
		const allSingleDetailEvals = reduce(newDetails, (prevPromise, newDetail) => {
			return prevPromise
				.then(ruleAcc => {
					const valuesByScope = {
						[ctrl.reportScopes.detail]: Object.values(newDetail.values),
						[ctrl.reportScopes.reportAndDetails]: [...headerVals].concat(Object.values(newDetail.values))

					};

					const processed = ctrl.ruleProvider.validate([ctrl.reportScopes.detail, ctrl.reportScopes.reportAndDetails], newDetail.detailId, valuesByScope)
						.then(triggeredRules => {
							ruleAcc = (ruleAcc || {});
							ruleAcc[newDetail.detailId] = {
								triggeredRules,
								detail: newDetail,
								detailId: newDetail.detailId
							};

							return ruleAcc;
						});

					return processed;
				});
		}, Promise.resolve())
			.then(evals => {
				hasBlockSave = checkEvalsForBlockSave(evals);
				return evals;
			});

		const allDetailEvals = allSingleDetailEvals.then(ruleAcc => {
			if(hasBlockSave === true) {
				return ruleAcc;
			}

			return runDetailsRules(newDetails).then(triggeredRules => {
				ruleAcc = (ruleAcc || {});
				ruleAcc[undefined] = {
					triggeredRules
				};

				hasBlockSave = checkEvalsForBlockSave({ rules: triggeredRules });
				return ruleAcc;
			});
		});

		return allDetailEvals.then(evals => {
			if(hasBlockSave === true) {
				$logger.event($logger.reportDetailEventBuilder("saveBlocked"), { evals });

				return evals;
			}

			forEach(evals, (currentDetailEval) => {
				if(currentDetailEval?.detail) {
					ctrl.reportValues[detailValuesKey] = {
						...ctrl.reportValues[detailValuesKey],
						[currentDetailEval.detailId]: currentDetailEval.detail
					};
					ctrl.eventBus.trigger(EVENT_DETAIL_DIRTY, { detailId: currentDetailEval.detailId });
					ctrl.validationService.updateTriggeredRules(currentDetailEval.triggeredRules, [ctrl.reportScopes.detail, ctrl.reportScopes.reportAndDetails], currentDetailEval?.detailId);
				}
				else {
					ctrl.validationService.updateTriggeredRules(currentDetailEval.triggeredRules, [ctrl.reportScopes.details]);
				}
			});

			ctrl.validationService.commitValidations();
			setReportState();
			$logger.event($logger.reportDetailEventBuilder("saved"), { evals });

			return evals;
		});
	}

	function checkEvalsForBlockSave(keyedEvals) {
		const evals = Object.values(keyedEvals);
		let checkBlockSave = false;
		for(let i = 0; i < evals.length; i++) {
			const currentDetailEval = evals[i];

			if(currentDetailEval.triggeredRules === false) {
				continue;
			}

			const blockSaveEvalRules = filter(currentDetailEval.triggeredRules, r => {
				const hasBlockSaveTrigger = some(r.Triggers, t => t.Operator.Code === TRIGGER_OPERATOR_CODES.blockSave);
				return hasBlockSaveTrigger;
			});

			if(blockSave(blockSaveEvalRules)) {
				currentDetailEval.blockSave = true;
				checkBlockSave = true;
			}
		}

		return checkBlockSave;
	}

	function runDetailsRules(newDetails, detailId) {
		const headerVals = ctrl.getFormValues();
		const newDetailTableVals = {
			...ctrl.reportValues[detailValuesKey]
		};

		if(newDetails) {
			forEach(newDetails, d => newDetailTableVals[d.detailId] = d);
		}

		const allDetails = {};

		allDetails[ctrl.reportScopes.details] = filter(flatMapDeep(newDetailTableVals, d => {
			if(d.isDeleted) {
				return undefined;
			}

			const detailValues = Object.values(d.values);
			return detailValues.map(v => ({
				...v,
				DetailId: d.detailId
			}));
		}), d => d !== undefined);
		allDetails[ctrl.reportScopes.reportAndDetails] = [...headerVals].concat(...allDetails[ctrl.reportScopes.details]);
		const scopes = [ctrl.reportScopes.details, ctrl.reportScopes.reportAndDetails];
		if(detailId !== undefined) {
			scopes.push(ctrl.reportScopes.detail);
			allDetails[ctrl.reportScopes.detail] = !newDetailTableVals[detailId].isDeleted ?
				allDetails[ctrl.reportScopes.details].filter(d => d.DetailId === detailId) :
				undefined;
		}

		return ctrl.ruleProvider.validate(scopes, detailId, allDetails);
	}

	function setReportState() {
		ctrl.validationScopes = ctrl.validationScopes.length > 0
			? ctrl.validationScopes
				: [ctrl.reportScopes.report, ctrl.reportScopes.details, ctrl.reportScopes.reportAndDetails, ctrl.reportScopes.metadata];

		ctrl.reportMetadata.validations = ctrl.validationService.ruleValidations;

		ctrl.reportMetadata.hasErrors = some(ctrl.reportMetadata.validations,
			v => (v.code === TRIGGER_OPERATOR_CODES.blockSave || v.code === TRIGGER_OPERATOR_CODES.blockSubmit) && v.rules.length);
		ctrl.reportMetadata.hasWarnings = some(ctrl.reportMetadata.validations, v => v.code === TRIGGER_OPERATOR_CODES.warning && v.rules.length);

		ctrl.setValidationsForDetails && ctrl.setValidationsForDetails(ctrl.reportMetadata.validations);
	}

	function processRules(ruleScopes, detailId, commit = true, values) {
		return ctrl.ruleProvider.validate(ruleScopes, detailId, values).then(rules => {
			ctrl.validationService.updateTriggeredRules(rules, ruleScopes, detailId);
			commit && ctrl.validationService.commitValidations();
			setReportState();
			return rules;
		});
	}

	function initializeRules() {
		const registeredConditionFieldIds = [];

		const uiRules = filter(ctrl.form.Rules, r => some(r.Triggers, t => t.Type.Code === TRIGGER_TYPES.userInterface));
		const visibilityRules = filter(uiRules, r => some(r.Triggers, t =>
			t.Operator.Code === UI_OPERATOR_TYPES.visibility
			|| t.Operator.Code === UI_OPERATOR_TYPES.details
			|| t.Operator.Code === UI_OPERATOR_TYPES.readonly
		));
		const filterRules = filter(uiRules, r => some(r.Triggers, t => t.Operator.Code === UI_OPERATOR_TYPES.filter));

		let ruleProcessingPromise;

		ctrl.eventBus.register(
			EVENT_PROCESS_UI_RULES,
			(e, params) => {
				if(ctrl.pageIsLoaded !== true) {
					return;
				}
				if(!ctrl.isProcessingRules) {
					ruleProcessingPromise = runProcessVisibilityRules(params);
				}
				else if(params.force) {
					ruleProcessingPromise = ruleProcessingPromise.then((lastProcess) => {
						return runProcessVisibilityRules(lastProcess.params);
					});
				}
			},
			$scope
		);

		ctrl.eventBus.register(
			EVENT_PROCESS_FILTER_RULES,
			(e, params) => {
				const repeatFilteredValues = (params.values ?? [])
					.filter(v => v.repeatId === undefined || v.repeatId === params.repeatId);

				runProcessFilterRules(params.ruleIds, repeatFilteredValues, params.conditionFieldId, params.repeatId);
			},
			$scope
		);

		ctrl.eventBus.register(
			EVENT_PROCESS_VALIDATIONS,
			(e, params) => {
				ctrl.processRules(params.scopes);
			},
			$scope
		);

		ctrl.fieldFilterRuleMap = {};

		uiRules.forEach(r => {
			const triggerTypes = r.Triggers.map(t => t.Operator.Code);

			initializeConditionsOrChildren(r, r.Id, triggerTypes);
			r.Triggers
				.filter(t => t.Type.Code === TRIGGER_TYPES.userInterface)
				.forEach(t => {
					const field = find(ctrl.form.Fields, { Id: t.Field?.Id });

					t.Field = field;
				});
		});

		forEach(ctrl.fieldFilterRuleMap, (fr) => {
			fr.ruleIds = uniq(fr.ruleIds);

			ctrl.eventBus.register(
				EVENT_VALUE_CHANGED(fr.conditionFieldId),
				(_, params) => {
					const valueScopes = [REPORT_SCOPES.report];

					if(params.field.IsDetail) {
						if(!ctrl.detailModalInstance) { //do not run filters if modal is not rendered yet
							return;
						}

						valueScopes.push(REPORT_SCOPES.detail);
					}

					const rpvalues = ctrl.ruleProvider.getValuesByType(valueScopes);

					const allValues = Object.values(rpvalues).reduce((acc, next) => acc.concat(next), []);

					ctrl.eventBus.trigger(EVENT_PROCESS_FILTER_RULES, {
						values: allValues,
						ruleIds: fr.ruleIds,
						conditionFieldId: fr.conditionFieldId,
						field: params.field
					});
				},
				$scope
			);
		});

		const registeredFilterFieldIds = Object.values(ctrl.fieldFilterRuleMap).map(v => v.conditionFieldId);
		ctrl.eventBus.register(
			EVENT_TRIGGER_FILTER_RULES,
			(_, params) => {
				if(registeredFilterFieldIds.indexOf(params.field.Id) < 0) {
					return;
				}

				const valueScopes = [REPORT_SCOPES.report];

				if(params.field.IsDetail) {
					valueScopes.push(REPORT_SCOPES.detail);
				}

				const rpvalues = ctrl.ruleProvider.getValuesByType(valueScopes);

				const allValues = Object.values(rpvalues).reduce((acc, next) => acc.concat(next), []);

				ctrl.eventBus.trigger(EVENT_PROCESS_FILTER_RULES, {
					values: allValues,
					ruleIds: ctrl.fieldFilterRuleMap[params.field.Id].ruleIds,
					fieldId: params.field.Id,
					repeatId: params.repeatId
				});
			},
			ctrl.scope
		);

		const preemptiveRangeRules = filter(ctrl.form.Rules, r => r.Engine && r.Engine.Code === ENGINE_EVALUATIONS.range.code && r.Operator && r.Operator.Code === RULE_OPERATORS.AND);

		preemptiveRangeRules.forEach(({ Conditions }) => {
			Conditions.forEach(c => {
				const field = find(ctrl.form.Fields, { Id: c.Field.Id });
				if(field && field.Template === FIELD_TEMPLATES.Number) {
					field.MinValue = c.MinValue;
					field.MaxValue = c.MaxValue;
				}
			});
		});

		const preemptiveCompareRules = filter(
			ctrl.form.Rules,
			r =>
				r.Engine &&
				r.Engine.Code === ENGINE_EVALUATIONS.compare.code &&
				r.Operator &&
				r.Operator.Code === RULE_OPERATORS.AND &&
				r.Conditions &&
				every(r.Conditions, c => Object.prototype.hasOwnProperty.call(c, "Value"))
		);

		preemptiveCompareRules.forEach(({ Conditions }) => {
			Conditions.forEach(c => {
				const field = find(ctrl.form.Fields, { Id: c.Field.Id });

				if(field && field.Template === FIELD_TEMPLATES.Number) {
					const limit = field.IncrementingStep ? c.Value * field.IncrementingStep : c.Value;

					switch(c.Operator.Code) {
						case CONDITION_EVALUATIONS.greaterThan:
							field.MinValue = limit + (field.IncrementingStep || 1);
							break;
						case CONDITION_EVALUATIONS.greaterThanOrEqualTo:
							field.MinValue = limit;
							break;
						case CONDITION_EVALUATIONS.lessThan:
							field.MaxValue = limit - (field.IncrementingStep || 1);
							break;
						case CONDITION_EVALUATIONS.lessThanOrEqualTo:
							field.MaxValue = limit;
							break;
						default:
							break;
					}
				}
			});
		});

		function initializeConditionsOrChildren(r, rootRuleId, triggerTypes, ruleTriggerFieldIds) {
			const newRuleTriggerFieldIds = (r.Triggers || [])
				.filter(t => t.Field?.Id !== undefined)
				.map(t => t.Field.Id)
				.concat(ruleTriggerFieldIds || []);

			if(!!r.Conditions) {
				r.Conditions.forEach(c => {
					if(c.Field) {
						const conditionField = find(ctrl.form.Fields, { Id: c.Field.Id });
						if(conditionField) {
							if(triggerTypes.indexOf(UI_OPERATOR_TYPES.filter) > -1) {
								// note: ctrl.fieldFilterRuleMap is a map from conditionFieldId => [ruleIds]
								if(ctrl.fieldFilterRuleMap[conditionField.Id] === undefined) {
									ctrl.fieldFilterRuleMap[conditionField.Id] = { conditionFieldId: conditionField.Id, ruleIds: [] };
								}
								ctrl.fieldFilterRuleMap[conditionField.Id].ruleIds.push(rootRuleId);
								if(c.Scope === undefined) {
									c.Scope = "Order";
								}
							}

							if(registeredConditionFieldIds.indexOf(c.Field.Id) > -1) {
								return;
							}

							if(triggerTypes.indexOf(UI_OPERATOR_TYPES.visibility) > -1
								|| triggerTypes.indexOf(UI_OPERATOR_TYPES.details) > -1
								|| triggerTypes.indexOf(UI_OPERATOR_TYPES.readonly) > -1
							) {
								ctrl.eventBus.register(
									EVENT_VALUE_CHANGED(conditionField.Id),
									(e, params) => {
										ctrl.eventBus.trigger(EVENT_PROCESS_UI_RULES, { value: params.value, field: params.field });
									},
									$scope
								);
								registeredConditionFieldIds.push(c.Field.Id);
							}
						}
					}
				});
			} else {
				r.Children.forEach(childRule => {
					initializeConditionsOrChildren(childRule, rootRuleId, triggerTypes, newRuleTriggerFieldIds);
				});
			}
		}

		function runProcessVisibilityRules(params) {
			ctrl.isProcessingRules = true;
			ctrl.isProcessingQueueInUse = true;
			if(ctrl.processingQueueTimer) {
				clearTimeout(ctrl.processingQueueTimer);
			}

			const sectionScope = ((params.field && params.field.IsDetail) || params.isDetail) ? REPORT_SCOPES.detail : REPORT_SCOPES.report;
			const runScopes = [REPORT_SCOPES.reportAndDetails, REPORT_SCOPES.details, REPORT_SCOPES.metadata, sectionScope];

			const allValuesByScope = ctrl.ruleProvider.getValuesByType([REPORT_SCOPES.reportAndDetails, REPORT_SCOPES.detail]);
			let detailScopedValues = allValuesByScope[REPORT_SCOPES.detail];

			if(
				detailScopedValues !== undefined
				&& detailScopedValues.length === 0
				&& (params.detailId !== undefined || ctrl.currentDetailId !== undefined)
			) {
				detailScopedValues = [{ DetailId: (params.detailId ?? ctrl.currentDetailId) }];
			}

			const allValues = detailHelper.mergeDetails([allValuesByScope[REPORT_SCOPES.reportAndDetails] ?? [], detailScopedValues ?? []]);
			const ruleProcesses = [];

			const visibleFieldIds = ctrl.form.Fields
				.filter(f => f.visible !== false)
				.map(f => f.Id);

			let detailId = ctrl.currentDetailId;
			if(sectionScope === REPORT_SCOPES.detail || params.detailId !== undefined) {
				if(params.detailId !== undefined) {
					detailId = params.detailId;
				}
				else {
					const thisDetailValues = ctrl.ruleProvider.getValuesByType([REPORT_SCOPES.detail]);

					const detailItem = (thisDetailValues[REPORT_SCOPES.detail] ?? []).find(v => v.DetailId !== undefined);
					detailId = detailItem?.DetailId;
				}
			}

			forEach(runScopes, scope => {
				const rulesToProcess = visibilityRules.filter(rule => {
					return rule.Scope.Code === scope;
				});

				//filter values to visible values in header or current detail but NOT in other details
				const visibleValues = allValues.filter(v => (v.DetailId !== undefined && v.DetailId !== 0) || visibleFieldIds.indexOf(v.FieldId) > -1);
				if(rulesToProcess.length) {
					const processed = ctrl.ruleProvider.process(rulesToProcess, visibleValues, detailId, scope);

					ruleProcesses.push(processed);
				}
			});

			return Promise.all(ruleProcesses).then((rules) => {
				ctrl.isProcessingRules = false;

				// After visibility rules, check if any fields became newly visible.
				// If so, re-trigger filter rules so their options get populated.
				const hasNewlyVisibleField = ctrl.form.Fields
					.some(f => f.visible !== false && visibleFieldIds.indexOf(f.Id) === -1);

				if(hasNewlyVisibleField) {
					const filterValueScopes = [REPORT_SCOPES.report];
					if(sectionScope === REPORT_SCOPES.detail) {
						filterValueScopes.push(REPORT_SCOPES.detail);
					}
					const rpvalues = ctrl.ruleProvider.getValuesByType(filterValueScopes);
					const allFilterValues = Object.values(rpvalues).reduce((acc, next) => acc.concat(next), []);
					ctrl.eventBus.trigger(EVENT_PROCESS_FILTER_RULES, { values: allFilterValues });
				}

				ctrl.processingQueueTimer = setTimeout(() => {
					ctrl.isProcessingQueueUnsettled = false;
					ctrl.processingQueueTimer = undefined;

					ctrl.eventBus.trigger(EVENT_PROCESS_RULES_SETTLED);
					$scope.$apply();
				}, 100);

				$logger.groupEnd();

				return { rules, params };
			});
		}

		function runProcessFilterRules(ruleIds, values, conditionFieldId, repeatId) {
			const thisFilterRules = ruleIds?.length > 0 ? filter(filterRules, r => ruleIds.indexOf(r.Id) > -1) : filterRules;
			filterService.executeFilterTriggers(thisFilterRules, values, ctrl.currentDetailId, conditionFieldId, repeatId, ctrl.reportScope);
		}
	}

	function setApiValidations(validations, showHeaderValidationsOnly = false) {
		ctrl.validationService.updateApiValidations(validations);

		ctrl.validationScopes = [ctrl.reportScopes.report, ctrl.reportScopes.reportAndDetails, ctrl.reportScopes.metadata];

		if(!showHeaderValidationsOnly) {
			ctrl.validationScopes.push(ctrl.reportScopes.details);
		}
	}

	async function handleRestore() {
		if(ctrl.isMinimalMode()) {
			return;
		}

		try {
			await loadingService.watch(ctrl.reportScope.actions.restore(ctrl.report));
			ctrl.eventBus.trigger(EVENT_REPORT_UNLOCKED);

			toastr.success("Report restored successfully, and is pending completion.");
			$logger.event($logger.buildReportEvent("restore")("succeeded"), {
				reportId: ctrl.reportMetadata.report.Id,
				via: "edit-report"
			});
		}
		catch(err) {
			$logger.error(err);
		}
	}

	async function handleWithdraw() {
		if(ctrl.isMinimalMode()) {
			return;
		}

		const confirmWithdraw = await confirmWithdrawReport(ctrl.form.nomenclature);

		if(!confirmWithdraw)
			return;

		try {
			await loadingService.watch(ctrl.reportScope.actions.withdraw(ctrl.report));

			ctrl.eventBus.trigger(EVENT_REPORT_UNLOCKED);
			triggerPrimaryDateFilter();
			toastr.success(`You may now modify this ${ctrl.form.nomenclature.PositiveReport}. Please make sure you complete again when finished.`);
			$logger.event($logger.buildReportEvent("withdraw")("succeeded"), {
				reportId: ctrl.reportMetadata.report.Id,
				via: "edit-report"
			});
		}
		catch(err) {
			$logger.error(err);
		}
	}

	async function handleDelete() {
		if(ctrl.isMinimalMode()) {
			return;
		}

		const continueCheck = ctrl.isReportDirty ? await confirmUnsavedChangesWillBeLost() : true;
		if(!continueCheck) {
			return;
		}

		try {
			// wipe details here instead of on getting all values to avoid wiping them while the report is in an intermediary state
			if(ctrl.suppressDetails) {
				ctrl.reportValues[detailValuesKey] = {};
			}

			ctrl.eventBus.trigger(EVENT_REVERT_VALUES, { values: { ...ctrl.report } });

			await loadingService.watch(ctrl.reportScope.actions.delete(ctrl.report));

			ctrl.eventBus.trigger(EVENT_REPORT_LOCKED);
			toastr.success(`This ${ctrl.form.nomenclature.PositiveReport} has been deleted.`);
			$logger.event($logger.buildReportEvent("delete")("succeeded"), {
				reportId: ctrl.reportMetadata.report.Id,
				via: "edit-report"
			});
		}
		catch(resp) {
			ctrl.reportMetadata.report.hasErrors = true;
			if(resp.Data?.Rules) {
				resp.Data.Rules.forEach(r => {
					if(r.Detail && r.Detail.Id === undefined && r.Detail.LinkKey !== undefined) {
						const matchingVal = Object.values(ctrl.reportValues[detailValuesKey])
							.find(detail => Object.values(detail.values).some(v => v.DetailLinkKey === r.Detail.LinkKey));
						r.Detail.Id = matchingVal.detailId;
					}
				});

				setApiValidations(resp.Data.Rules, true);
				ctrl.validationService.commitValidations();
				setReportState();

				$logger.event(reportSaveEventBuilder("failed"), {
					serverRules: resp.Data.Rules
				});
			}

			$logger.event(reportSaveEventBuilder("serverErrored"));
		}
	}

	async function handleSubmit(route) {
		if(ctrl.isMinimalMode()) {
			return;
		}

		if(ctrl.reportMetadata.hasErrors) {
			return;
		}
		triggerPrimaryDateFilter();
		updateReportDirty();

		const submitRequestEvent = submitEventBuilder("requested");

		$timeout(() => {
			const runSubmit = async() => {
				let createdReport;

				const transitionToCreatedReport = (rules) => {
					if(createdReport) {
						transitionToEditReport({
							...createdReport,
							Rules: rules ?? createdReport.Rules
						}, { isNewReport: true });
					}
				};

				if(ctrl.isReportDirty) {
					const dirtyFields = map(ctrl.dirtyHeaderFields, (_, key) => find(ctrl.form.Fields, field => field.Id == key));
					const dirtyFieldNames = dirtyFields.map(f => f.Name);
					const numDirtyDetails = size(ctrl.dirtyDetailTracking);

					if(hasHadState("PENDING", ctrl.report)) { // TODO: I think this should be ctrl.reportMetadata.report
						const continueWithDirtySubmission = await confirmDirtySubmission(dirtyFieldNames, numDirtyDetails);
						if(!continueWithDirtySubmission) {
							$logger.event(submitRequestEvent, {
								reportIsDirty: true,
								continueSubmission: false,
								dirtyFieldNames,
								dirtyDetailCount: numDirtyDetails
							});

							return;
						}
					}

					$logger.event(submitRequestEvent, {
						reportIsDirty: true,
						continueSubmission: true,
						dirtyFieldNames,
						dirtyDetailCount: numDirtyDetails
					});

					const saveResult = await saveReport();
					if(!saveResult) {
						return;
					}

					if(saveResult.isNewReport) {
						createdReport = saveResult.report;
					}
				}

				const activeBlockSave = ctrl.validationService.getRuleValidationsOfType(TRIGGER_OPERATOR_CODES.blockSave);
				if(activeBlockSave?.rules?.length ?? 0 > 0) {
					$logger.event(submitEventBuilder("blocked"), {
						saveBlocked: true,
						activeBlockSave
					});
					transitionToCreatedReport();
					return;
				}

				const activeBlockSubmit = ctrl.validationService.getRuleValidationsOfType(TRIGGER_OPERATOR_CODES.blockSubmit);
				if(activeBlockSubmit?.rules?.length ?? 0 > 0) {
					$logger.event(submitEventBuilder("blocked"), {
						submitBlocked: true,
						activeBlockSubmit
					});
					transitionToCreatedReport();
					return;
				}

				const activeWarnings = ctrl.validationService.getRuleValidationsOfType(TRIGGER_OPERATOR_CODES.warning);
				if(activeWarnings?.rules?.length ?? 0 > 0) {
					const confirmWarnings = await confirmRuleWarningsOnReport(activeWarnings);
					if(!confirmWarnings) {
						$logger.event(submitEventBuilder("cancelled"), {
							withWarnings: true,
							activeWarnings,
							confirmed: false
						});

						transitionToCreatedReport();
						return;
					}
					$logger.event(submitRequestEvent, {
						withWarnings: true,
						activeWarnings,
						confirmed: true
					});
				}
				else {
					$logger.event(submitRequestEvent);
				}

				const submitResult = await submitReport();

				if(submitResult?.report) {
					if(route) {
						$state.transitionTo(route, { via: "submit-button" }, { location: "replace", reload: route === NEW_REPORT ? BASE_REPORT : route });
						return;
					}

					if(createdReport) {
						transitionToEditReport(submitResult.report);
						return;
					}

					ctrl.eventBus.trigger(EVENT_REPORT_LOCKED);
					return submitResult.report;
				}

				transitionToCreatedReport(submitResult?.error?.Data?.Rules);
			};

			const submitPromise = runSubmit();
			loadingService.watch(submitPromise);
		});
	}

	function submitReport() {
		const promise = ctrl.reportScope.actions.submit(ctrl.reportMetadata.report).then(
			report => {
				toastr.success("Completion successful.");
				$logger.event(submitEventBuilder("succeeded"));

				return { report };
			},
			resp => {
				$logger.event(submitEventBuilder("failed"), { rules: resp?.Data?.Rules });
				if((resp?.Data?.Rules || []).length) {
					setApiValidations(resp.Data.Rules);
					ctrl.validationService.commitValidations();
					setReportState();
				}

				return { error: resp };
			}
		);

		return loadingService.watch(promise);
	}

	function transitionToEditReport(report, { isNewReport = false } = {}) {
		return $state.go(
			EDIT_REPORT,
			{ id: report.Id, report },
			{
				location: "replace",
				reload: BASE_REPORT,
				custom: {
					showValidations: true,
					isNewReport
				}
			}
		);
	}

	function updateRuleMetadata(report) {
		const updatedLMreport = {
			...report,
			LastModified: new Date()
		};

		setReportMetadata(updatedLMreport);

		setFieldOptions(ctrl.form.Fields);

		setApiValidations(report.Rules, false);
		ctrl.validationService.commitValidations();
		setReportState();

		ctrl.eventBus.trigger(EVENT_PROCESS_UI_RULES);
	}

	function triggerFilters(fieldsToTrigger, reportValues, isInitializing = true) {
		const triggerFields = fieldsToTrigger || ctrl.form.Fields;
		const triggerValues = reportValues || map(ctrl.reportValues[headerValuesKey]);

		const filters = filter(ctrl.form.Features, f => f.Type === FEATURE_TYPES.filter);

		filters.forEach(f => {
			const initiator = minBy(f.Fields, "Order");
			const initiatorField = find(triggerFields, f => f.Id === initiator.Field.Id);

			if(initiatorField) {
				let thisFilterValue = triggerValues
					.filter(reportField => {
						return reportField.FieldId === initiator.Field.Id;
					})
					.map(reportVal => {
						return reportVal.DataId ?? reportVal.EntityId;
					});

				if(thisFilterValue.length < 1) {
					thisFilterValue = null;
				}

				const params = {
					field: initiatorField,
					value: thisFilterValue
				};
				if(isInitializing) {
					params.initialValue = true;
				}

				ctrl.eventBus.trigger(EVENT_VALUE_CHANGED(initiator.Field.Id), params);
				ctrl.eventBus.trigger(EVENT_HANDLE_SUCCESSOR_LOCK(initiator.Field.Id), { enable: true });
			}
		});
	}

	function triggerAreaFilters() {
		const areaFilters = filter(ctrl.form.Features, f => f.Type === FEATURE_TYPES.mapPoint);

		areaFilters.forEach(f => {
			const initiator = minBy(f.Fields, "Order");
			const initiatorField = find(ctrl.form.Fields, f => f.Id === initiator.Field.Id);

			const reportValue = find(ctrl.reportValues[headerValuesKey], reportField => {
				return reportField.FieldId === initiator.Field.Id;
			});

			if(reportValue) {
				const selectedEntity = find(initiatorField.Data, item => {
					return item.Id === reportValue.EntityId;
				});

				ctrl.eventBus.trigger(EVENT_VALUE_CHANGED(initiator.Field.Id), {
					field: initiatorField,
					value: selectedEntity
				});
			}
		});
	}

	function triggerPrimaryDateFilter(values, autoSelect = true) {
		values = values ? groupReportValues(values) : ctrl.reportValues;

		const primaryDateValue = primaryDateFilter(values);

		if(!!primaryDateValue) {
			const primaryDateField = ctrl.form.Fields.find(field => field.Type.Code == "DATE" && field.IsPrimary == true);
			const useDay = primaryDateField?.Template === FIELD_TEMPLATES.Date;
			const useMonth = primaryDateField?.Template === FIELD_TEMPLATES.YearMonth;
			const asOf = moment(primaryDateValue.Date);
			const effectiveAsOf =
				useDay ?
				{
					fromInclusive: asOf.clone().startOf("day"),
					toExclusive: asOf.clone().add(1, "day")
						.startOf("day"),
					allowPartial: true
				}
				: useMonth ?
				{
					fromInclusive: asOf.clone().startOf("month"),
					toExclusive: asOf.clone().add(1, "month")
						.startOf("month"),
					allowPartial: true
				}
				: // point in time
				{ fromInclusive: asOf, toExclusive: asOf, allowPartial: true };
			ctrl.eventBus.trigger(EVENT_FILTER_PRIMARYDATE, { effectiveAsOf, autoSelect });
		}
	}

	function primaryDateFilter(values) {
		const primaryDateFields = ctrl.form.Fields.filter(field => field.Type.Code == "DATE" && field.IsPrimary == true);

		if(primaryDateFields.length !== 1) {
			$logger.error("Detected more than 1 primary date field", { form: ctrl.form.Name });
			return;
		}

		const primaryDateField = primaryDateFields[0];
		const primaryDateValue = find(values[headerValuesKey], f => f.FieldId === primaryDateField.Id);

		return primaryDateValue;
	}

	function prevReport() {
		if(ctrl.isMinimalMode()) {
			return;
		}

		const additionalHeaders = { Prefer: "return=representation" };
		const promise = api.get(FORM_REPORT_PREVIOUS(ctrl.form.Id, ctrl.reportMetadata.report.Id), { headers: additionalHeaders }).then(resp => {
			updateReport(resp);
		})
			.catch(() => toastr.error(`Previous ${ctrl.form.nomenclature.PositiveReport} not found.`));


		loadingService.watch(promise);
	}

	function nextReport() {
		if(ctrl.isMinimalMode()) {
			return;
		}

		const additionalHeaders = { Prefer: "return=representation" };
		const promise = api.get(FORM_REPORT_NEXT(ctrl.form.Id, ctrl.reportMetadata.report.Id), { headers: additionalHeaders }).then(resp => {
			updateReport(resp);
		})
			.catch(() => toastr.error(`Next ${ctrl.form.nomenclature.PositiveReport} not found.`));

		loadingService.watch(promise);
	}

	function createReport() {
		if(ctrl.isMinimalMode()) {
			return;
		}
		$state.go(NEW_REPORT, { via: "create-new-button" }, { location: true, reload: BASE_REPORT });
	}

	function updateReport(resp) {
		$state.go(EDIT_REPORT, { id: resp.Id, report: null }, { reload: BASE_REPORT });
	}

	function handleDetailSelected(detail) {
		ctrl.editDetail(detail);
	}

	function editDetail(detail) {
		if(!ctrl.detailModalIsOpen) {
			ctrl.detailModalIsOpen = true;
			let detailValues = undefined;
			ctrl.currentDetailId = detail?.detailId ?? 0;

			$logger.event($logger.reportDetailEventBuilder("editing"), { detailId: ctrl.currentDetailId });

			if(!!detail) {
				const valueDictionary = ctrl.reportValues[detailValuesKey][detail.detailId].values;
				detailValues = map(valueDictionary, val => val);
			}

			const allDetailValues = reduce(ctrl.reportValues[detailValuesKey], (acc, val) => {
				const thisDetailValueArray = map(val.values);
				return acc.concat(thisDetailValueArray);
			}, []);

			const instance = $uibModal.open({
				component: "report-details-modal",
				size: "lg",
				backdrop: "static",
				// disable default ESC handling so the modal can control it and
				// prompt the user when dirty
				keyboard: false,
				resolve: {
					reportScope: () => ctrl.reportScope,
					form: () => ctrl.form,
					reportId: () => ctrl.reportMetadata.report.Id,
					values: () => detailValues,
					detailId: () => detail?.detailId,
					rules: () => ctrl.form.Rules,
					allValues: () => allDetailValues,
					triggerFieldFilters: () => {
						return (values) => {
							const headerVals = ctrl.ruleProvider.getValuesByType(["REPORT"]);
							const filterValues = (values || []).concat(headerVals["REPORT"]);

							ctrl.triggerFilters(ctrl.form.Fields, filterValues, false);
							triggerPrimaryDateFilter(filterValues);

							ctrl.eventBus.trigger(EVENT_PROCESS_FILTER_RULES, { values: filterValues });
						};
					},
					initializeFields: () => {
						return (fields) => {
							fields.map(field => initializeNewField(field, fields));
							triggerPrimaryDateFilter(getAllValues());
						};
					},
					onSave: () => {
						return updates => {
							return ctrl.handleDetailSave(updates);
						};
					},
					valueProviderRegistrar: () => ctrl.ruleProvider.registerValueProvider,
					headerModifiedDates: () => {
						return { Created: ctrl.reportMetadata.report.Created, LastModified: ctrl.reportMetadata.report.LastModified };
					}
				}
			});

			instance.rendered.then(() => (ctrl.detailModalInstance = instance));

			instance.result.finally(() => {
				ctrl.currentDetailId = undefined;
				ctrl.form.detailFields.map(f => (f.isLocked = false));
				ctrl.detailModalInstance = null;
				ctrl.detailModalIsOpen = false;
				ctrl.eventBus.trigger(EVENT_PROCESS_UI_RULES, {});

				$logger.event($logger.reportDetailEventBuilder("modalClosed"), { detailId: ctrl.currentDetailId });
			});
		}
	}

	ctrl.copyToNew = () => {
		if(ctrl.isMinimalMode()) {
			return;
		}

		const allReportValues = getAllValues(true);
		const merged = {
			...ctrl.reportMetadata.report,
			Fields: [...allReportValues]
		};

		delete merged.Created;
		merged.History = [];
		delete merged.Id;
		delete merged.IsDeleted;
		delete merged.LastModified;
		delete merged.Number;
		delete merged.etag;
		delete merged.Date;
		delete merged.EntityId;
		delete merged.FormId;
		delete merged.TimeZone;

		const detailIdToLinkKeyMap = {};

		for(const v of merged.Fields) {
			if(v.DetailId === undefined && v.Detail?.Id === undefined) {
				continue;
			}

			const thisDetailId = v.DetailId ?? v.Detail?.Id;

			if(detailIdToLinkKeyMap[thisDetailId] === undefined) {
				detailIdToLinkKeyMap[thisDetailId] = [uuid(), uuid()];
			}

			v.DetailLinkKey = detailIdToLinkKeyMap[thisDetailId][0];
			v.DetailId = detailIdToLinkKeyMap[thisDetailId][1];

			delete v.Detail;
		}

		$state.go(NEW_REPORT, {
			via: "copy",
			id: undefined,
			report: merged
		}, {
			location: "replace",
			custom: {
				showValidations: true,
				isNewReport: true
			}
		});
	};
}

export default angular.module("vesl.components.manage-report", [multiSectionWarning]).component("manageReport", {
	template,
	controller,
	bindings: { report: "<" },
	require: {
		ruleProvider: "^ruleProvider",
		reportScope: "^^reportScope"
	}
}).name;
